/**
 * Worker Initialization Utilities
 * Contains functions for initializing workers with different configuration approaches
 */

import { Logger } from '@/shared/logger/logger';

/**
 * Configuration for a worker
 */
export type WorkerConfig = {
  /**
   * Bundle name of the worker
   */
  bundleName: string;
  /**
   * Current environment (dev, prod, etc.)
   */
  environment: string;
  /**
   * Bundle name of the main thread
   */
  mainThreadBundleName?: string;
  /**
   * Origin identifier for the worker
   */
  origin: string;
};

/**
 * Extract worker configuration from URL query parameters
 * @returns Worker configuration extracted from URL
 */
export function extractConfigFromUrl(): Partial<WorkerConfig> {
  const config: Partial<WorkerConfig> = {};

  try {
    if (self.location?.search) {
      const params = new URLSearchParams(self.location.search);

      // Extract environment
      const envParam = params.get('env');
      if (envParam) {
        config.environment = envParam;
      }

      // Extract bundle name
      const bundleParam = params.get('bundle');
      if (bundleParam) {
        config.bundleName = bundleParam;
      }

      // Extract main thread bundle name
      const mainBundleParam = params.get('mainBundle');
      if (mainBundleParam) {
        config.mainThreadBundleName = mainBundleParam;
      }

      // Extract origin
      const originParam = params.get('origin');
      if (originParam) {
        config.origin = originParam;
      }
    }

    // Also check URL path for environment indications as fallback
    if (!config.environment && self.location?.pathname) {
      const pathParts = self.location.pathname.split('/');
      const envIndicators = new Set([
        'dev',
        'development',
        'prod',
        'production',
        'stage',
        'staging',
        'test',
        'local',
      ]);

      for (const part of pathParts) {
        const normalized = part.toLowerCase();
        if (envIndicators.has(normalized)) {
          config.environment = normalized;
          break;
        }
      }
    }
  } catch (error) {
    // Silently fail and return what we have
    Logger.warn('Error extracting config from URL:', error);
  }

  return config;
}

/**
 * Combine configurations from multiple sources with priority
 * @param configs - Array of partial configurations in priority order (first has highest priority)
 * @returns Combined configuration with fallbacks
 */
export function combineConfigs(configs: Partial<WorkerConfig>[]): WorkerConfig {
  const BASE_36_RADIX = 36;
  const combined: WorkerConfig = {
    bundleName: 'unknown',
    environment: 'unknown',
    origin: `worker_${Date.now()}_${Math.random().toString(BASE_36_RADIX).slice(2, 9)}`,
  };

  // Apply configs in reverse order (lower priority first)
  for (let i = configs.length - 1; i >= 0; i--) {
    const config = configs[i];

    if (config?.environment) {
      combined.environment = config.environment;
    }

    if (config?.bundleName) {
      combined.bundleName = config.bundleName;
    }

    if (config?.mainThreadBundleName) {
      combined.mainThreadBundleName = config.mainThreadBundleName;
    }

    if (config?.origin) {
      combined.origin = config.origin;
    }
  }

  return combined;
}

/**
 * Initialize worker with URL config approach and log reliability
 * @param workerType - Type of worker (EWS, Events, etc.)
 * @param defaultBundleName - Default bundle name if not provided in config
 * @returns Worker origin
 */
export async function initializeWorkerWithAllApproaches(
  workerType: string,
  defaultBundleName: string,
): Promise<string> {
  return Logger.profileFnAsync(
    'initializeWorkerWithAllApproaches',
    async () => {
      Logger.info(`Initializing ${workerType} worker...`);

      // Get configuration from URL
      const urlConfig = extractConfigFromUrl();

      // Log reliability of URL approach
      Logger.info(`[${workerType}] URL Config:`, urlConfig);

      // Create a default config
      const defaultConfig: Partial<WorkerConfig> = {
        bundleName: defaultBundleName,
        environment: 'unknown',
      };

      // Combine configs with priority: URL > Default
      const finalConfig = combineConfigs([urlConfig, defaultConfig]);

      // Apply configuration
      Logger.setEnvironment(finalConfig.environment);

      // Disable unique profile keys to avoid duplicate logs for the same function
      Logger.setUseUniqueProfileKeys(false);

      // Set combined bundle name if main thread bundle name is provided
      if (finalConfig.mainThreadBundleName) {
        const combinedName = `${finalConfig.bundleName}|${finalConfig.mainThreadBundleName}`;
        Logger.setBundleName(combinedName);
      } else {
        Logger.setBundleName(finalConfig.bundleName);
      }

      Logger.info(
        `[${workerType}] Worker initialized with environment: ${finalConfig.environment}`,
      );
      Logger.info(
        `[${workerType}] Worker bundle name: ${finalConfig.bundleName}`,
      );

      // Ensure worker flag is set
      if (!Logger.isWorker) {
        Logger.isWorker = true;
      }

      return finalConfig.origin;
    },
  );
}
